import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Spin, Table, Tag } from 'antd';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';

// CUSTOM
import { tableSize, tableYScroll, tableHeaders } from '../settings';

const type = 'DraggableBodyRow';

const DraggableBodyRow = ({ index, moveRow, className, style, ...restProps }) => {
	const ref = useRef();
	const [{ isOver, dropClassName }, drop] = useDrop({
		accept: type,
		collect: (monitor) => {
			const { dragIndex } = monitor.getItem() || {};
			if (dragIndex === index) {
				return {};
			}
			return {
				isOver: monitor.isOver(),
				dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
			};
		},
		drop: (item) => {
			moveRow(item.dragIndex, index);
		},
	});
	const [, drag] = useDrag({
		item: { type, dragIndex: index },
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
	});
	drop(drag(ref));
	return (
		<tr
			ref={ref}
			className={`${className}${isOver ? dropClassName : ''}`}
			style={{ cursor: 'move', ...style }}
			{...restProps}
		/>
	);
};

const LabelRangeTable = ({ data, pageSize, loading = false, onRowDblClick, moveRow }) => {
	const { rangeLabelClass } = tableHeaders;
	
	const columns = [
		{ 
			title: rangeLabelClass.label.title,
			dataIndex: rangeLabelClass.label.dataIndex,
			width: 150,
			sorter: (a, b) => a.rangeClassLabel.localeCompare(b.rangeClassLabel)
		},
		{ 
			title: 'STATUS',
			dataIndex: 'active',
			width: 100,
			render: active => (
				<Tag color={active === 0 ? "red" : "green"}>
					{active === 0 ? 'INACTIVE' : 'ACTIVE'}
				</Tag>
			)
		}
	];

	const components = {
		body: {
			row: DraggableBodyRow,
		},
	};

	const moveRowWrapper = (dragIndex, hoverIndex) => {
		moveRow(dragIndex, hoverIndex);
	};

	return (
		<div style={{ marginTop: 20 }} className="settings-class-range-table">
			<Spin spinning={loading} tip="Loading...">
				<DndProvider backend={HTML5Backend}>
					<Table
						size={tableSize}
						pagination={{ pageSize, showSizeChanger: false }}
						columns={columns}
						dataSource={data}
						rowKey={record => record.rangeClassID}
						components={components}
						onRow={(record, index) => {
							const actualIndex = data.findIndex(item => item.rangeClassID === record.rangeClassID);
							return {
								index: actualIndex,
								moveRow: moveRowWrapper,
								onDoubleClick: () => onRowDblClick(record)
							};
						}}
					/>
				</DndProvider>
			</Spin>
		</div>
	);
};

LabelRangeTable.propTypes = {
	data: PropTypes.arrayOf(PropTypes.shape({
		rangeClassID: PropTypes.number.isRequired,
		rangeClassLabel: PropTypes.string.isRequired
	})).isRequired,
	pageSize: PropTypes.number.isRequired,
	loading: PropTypes.bool.isRequired,
	onRowDblClick: PropTypes.func.isRequired,
	moveRow: PropTypes.func.isRequired
};

export default LabelRangeTable;
